import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeftRight, Check, X, ExternalLink, Inbox, Send } from "lucide-react";
import { toast } from "sonner";

export default function Trades() {
  const [data, setData] = useState({ incoming: [], outgoing: [] });
  const [tab, setTab] = useState("incoming");

  const load = useCallback(async () => {
    const { data } = await api.get("/trades");
    setData(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const respond = async (id, status) => {
    await api.put(`/trades/${id}`, { status });
    toast.success(`Offer ${status}`);
    load();
  };

  const badge = (status) => {
    const m = { pending: "bg-amber-100 text-amber-700", accepted: "bg-emerald-100 text-emerald-700", declined: "bg-rose-100 text-rose-700" };
    return <span className={`text-[10px] font-tech uppercase px-2 py-0.5 rounded-full ${m[status] || ""}`}>{status}</span>;
  };

  const list = tab === "incoming" ? data.incoming : data.outgoing;

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-10">
      <p className="font-tech text-xs uppercase tracking-widest brand-text">Swaps</p>
      <h1 className="font-display text-4xl font-bold mb-6">Trade Offers</h1>

      <div className="flex gap-2 mb-6">
        <button data-testid="trades-tab-incoming" onClick={() => setTab("incoming")} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${tab === "incoming" ? "text-white" : "surface border"}`} style={tab === "incoming" ? { background: "var(--brand)" } : {}}><Inbox className="w-4 h-4" /> Incoming ({data.incoming.length})</button>
        <button data-testid="trades-tab-outgoing" onClick={() => setTab("outgoing")} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${tab === "outgoing" ? "text-white" : "surface border"}`} style={tab === "outgoing" ? { background: "var(--brand)" } : {}}><Send className="w-4 h-4" /> Outgoing ({data.outgoing.length})</button>
      </div>

      {list.length === 0 ? (
        <p className="text-muted-page py-10 text-center surface border rounded-2xl">No {tab} offers.</p>
      ) : (
        <div className="space-y-3" data-testid="trades-list">
          {list.map((t) => (
            <div key={t.id} className="surface border rounded-xl p-4" data-testid={`trade-${t.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <ArrowLeftRight className="w-4 h-4 brand-text" />
                  <span className="font-semibold">{tab === "incoming" ? `From ${t.from_name}` : `To ${t.to_name}`}</span>
                  {badge(t.status)}
                  {t.wishlist_match && <span className="text-[10px] font-tech uppercase px-2 py-0.5 rounded-full bg-amber-200 text-amber-900">⭐ Wishlist match</span>}
                  {t.external && <span className="text-[10px] font-tech uppercase px-2 py-0.5 rounded-full" style={{ background: "var(--honey-100)", color: "var(--honey-900)" }}>External · ${t.surcharge?.toFixed(2)}</span>}
                </div>
              </div>
              {t.insulator_name && <p className="text-sm">Re: <Link to={`/catalog/${t.insulator_id}`} className="brand-text">{t.insulator_name}</Link></p>}
              {t.message && <p className="text-sm text-muted-page mt-1">{t.message}</p>}
              {tab === "incoming" && t.status === "pending" && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <button data-testid={`accept-trade-${t.id}`} onClick={() => respond(t.id, "accepted")} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-emerald-600 text-white"><Check className="w-3 h-3" /> Accept</button>
                  <button data-testid={`decline-trade-${t.id}`} onClick={() => respond(t.id, "declined")} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200"><X className="w-3 h-3" /> Decline</button>
                  {t.external && t.paypal_link && <a href={t.paypal_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full text-white" style={{ background: "#0070ba" }}>Pay ${t.surcharge?.toFixed(2)} surcharge <ExternalLink className="w-3 h-3" /></a>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
